import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link, useLocation } from "@tanstack/react-router";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";

export function Analytics() {
  const [configs, setConfigs] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const location = useLocation();

  useEffect(() => {
    async function fetchConfigs() {
      const { data } = await supabase.from("configuracoes").select("*");
      if (data) {
        const configMap = data.reduce((acc: any, curr: any) => {
          acc[curr.chave] = curr.valor;
          return acc;
        }, {});
        setConfigs(configMap);
      }
    }
    fetchConfigs();

    const savedConsent = localStorage.getItem("cookie_consent");
    setConsent(savedConsent);
    
    // Check if enough time has passed for the banner to be relevant
    const consentDate = localStorage.getItem("cookie_consent_date");
    const isExpired = consentDate && (Date.now() - parseInt(consentDate)) > 365 * 24 * 60 * 60 * 1000;

    if (!savedConsent || isExpired) {
      setTimeout(() => setShowBanner(true), 1500);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookie_consent", "accepted");
    localStorage.setItem("cookie_consent_date", Date.now().toString());
    setConsent("accepted");
    setShowBanner(false);
  };

  const handleReject = () => {
    localStorage.setItem("cookie_consent", "rejected");
    localStorage.setItem("cookie_consent_date", Date.now().toString());
    setConsent("rejected");
    setShowBanner(false);
  };

  if (location.pathname.startsWith("/admin")) return null;

  const gaId = configs["ga_id"];
  const gtmId = configs["gtm_id"];
  const pixelId = configs["pixel_id"];
  const searchConsole = configs["search_console"];
  const customHead = configs["custom_head"];

  const isGaValid = gaId && /^G-[A-Z0-9]{10}$/.test(gaId);
  const isGtmValid = gtmId && /^GTM-[A-Z0-9]{4,10}$/.test(gtmId);
  const isPixelValid = pixelId && /^\d+$/.test(pixelId);

  return (
    <>
      <Helmet>
        {searchConsole && <meta name="google-site-verification" content={searchConsole} />}
        {consent === "accepted" && (
          <>
            {isGaValid && (
              <script async src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`} />
            )}
            {isGaValid && (
              <script>
                {`
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${gaId}');
                `}
              </script>
            )}
            {isGtmValid && (
              <script>
                {`
                  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer','${gtmId}');
                `}
              </script>
            )}
            {isPixelValid && (
              <script>
                {`
                  !function(f,b,e,v,n,t,s)
                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                  'https://connect.facebook.net/en_US/fbevents.js');
                  fbq('init', '${pixelId}');
                  fbq('track', 'PageView');
                `}
              </script>
            )}
            {customHead && <script dangerouslySetInnerHTML={{ __html: customHead }} />}
          </>
        )}
      </Helmet>
      {consent === "accepted" && isGtmValid && (
        <noscript>
          <iframe 
            src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
            height="0" width="0" style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      )}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 animate-in slide-in-from-bottom-full duration-500">
          <div className="container mx-auto max-w-5xl">
            <div className="bg-brand-dark text-white p-6 rounded-2xl shadow-2xl border border-white/10 flex flex-col md:flex-row items-center gap-6">
              <div className="bg-primary/20 p-3 rounded-xl text-primary shrink-0">
                <Cookie className="w-6 h-6" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <p className="text-sm leading-relaxed text-gray-300">
                  Usamos cookies para entender como a lista é usada e melhorar a experiência. 
                  Saiba mais na nossa <Link to="/politica-de-privacidade" className="text-primary hover:underline font-semibold">Política de Privacidade</Link>.
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-3 shrink-0">
                <Button 
                  variant="outline" 
                  className="bg-transparent border-white/20 text-white hover:bg-white/5 rounded-xl px-6"
                  onClick={handleReject}
                >
                  Rejeitar não essenciais
                </Button>
                <Button 
                  variant="onLight"
                  className="rounded-xl px-8 font-bold"
                  onClick={handleAccept}
                >
                  Aceitar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}